import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { ExternalLink, Search, Truck, Check, Circle, Package, ShoppingBag } from "lucide-react";

const CARRIER_LABEL = {
  usps: "USPS",
  ups: "UPS",
  fedex: "FedEx",
  dhl: "DHL",
  other: "Other",
};

// --- Timeline step (horizontal progress) ------------------------------
const TimelineStep = ({ label, sub, state, isLast }) => {
  // state: 'done' | 'active' | 'pending'
  const dotBg =
    state === "done"
      ? "bg-zinc-100 text-zinc-950 border-zinc-100"
      : state === "active"
      ? "bg-zinc-950 text-zinc-100 border-zinc-100"
      : "bg-zinc-950 text-zinc-600 border-zinc-700";
  const lineColor = state === "done" ? "bg-zinc-100" : "bg-zinc-800";
  const labelColor =
    state === "pending" ? "text-zinc-600" : "text-zinc-100";

  return (
    <div className="flex-1 flex items-start">
      <div className="flex flex-col items-center flex-shrink-0 relative z-10">
        <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${dotBg} transition-colors`}>
          {state === "done" ? <Check className="w-4 h-4" /> : <Circle className="w-2 h-2 fill-current" />}
        </div>
        <div className="mt-3 text-center">
          <div className={`font-display text-xs sm:text-sm uppercase tracking-widest ${labelColor}`}>
            {label}
          </div>
          <div className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500 mt-1 whitespace-nowrap">
            {sub}
          </div>
        </div>
      </div>
      {!isLast && (
        <div className={`flex-1 h-[1px] mt-4 -mx-4 ${lineColor} transition-colors`} />
      )}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// Guesstimate delivery window: shipped_at + 3–7 business days
const estDelivery = (shippedAt) => {
  if (!shippedAt) return null;
  try {
    const s = new Date(shippedAt);
    const from = new Date(s.getTime() + 3 * 86400000);
    const to = new Date(s.getTime() + 7 * 86400000);
    const opt = { month: "short", day: "numeric" };
    return `${from.toLocaleDateString(undefined, opt)} – ${to.toLocaleDateString(undefined, opt)}`;
  } catch {
    return null;
  }
};

const TrackOrderPage = () => {
  const location = useLocation();
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const o = p.get("order");
    const e = p.get("email");
    if (o) setOrderNumber(o);
    if (e) setEmail(e);
    if (o && e) submit(o, e);
  }, [location.search]);

  const submit = async (num, mail) => {
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", {
        order_number: num,
        email: mail,
      });
      setOrder(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;
    submit(orderNumber.trim(), email.trim());
  };

  const paid = order?.payment_status === "paid";
  const shipped = order?.fulfillment_status === "shipped";
  const eta = estDelivery(order?.shipped_at);

  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />

      {/* Hero header */}
      <section className="border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 grit pointer-events-none opacity-40" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-3 uppercase">
            Order tracking
          </div>
          <h1 className="display-h1 text-5xl sm:text-6xl md:text-7xl text-zinc-50">
            Where&apos;s my <span className="text-zinc-400">order?</span>
          </h1>
          <p className="mt-4 text-zinc-400 max-w-xl text-sm sm:text-base">
            Enter the order number from your confirmation email plus the email
            you used at checkout.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Lookup form */}
        <form
          onSubmit={handleSubmit}
          className="border border-zinc-800 bg-zinc-900/40 p-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
          data-testid="track-form"
        >
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-zinc-400 mb-1 block tracking-widest">
              Order number
            </label>
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              required
              placeholder="e.g. PDOB-4821"
              data-testid="track-order-number"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2.5 font-mono-tech text-sm text-zinc-100"
            />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-zinc-400 mb-1 block tracking-widest">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              data-testid="track-email"
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2.5 font-mono-tech text-sm text-zinc-100"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="track-submit"
            className="inline-flex items-center justify-center gap-2 bg-zinc-100 hover:bg-white disabled:opacity-60 text-zinc-950 px-6 py-2.5 font-display text-sm tracking-widest uppercase transition-colors h-[42px]"
          >
            <Search className="w-4 h-4" /> {loading ? "Looking..." : "Track"}
          </button>
          {error && (
            <div
              className="md:col-span-3 font-mono-tech text-xs uppercase text-red-400 tracking-widest"
              data-testid="track-error"
            >
              // {error}
            </div>
          )}
        </form>

        {/* Result panel */}
        {order && (
          <div className="mt-8 border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid="track-result">
            {/* Top ribbon */}
            <div className="border-b border-zinc-800 px-6 py-5 flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest">
                  Order
                </div>
                <div className="font-display text-2xl sm:text-3xl text-zinc-50 tracking-widest leading-none mt-1">
                  #{order.order_number}
                </div>
                <div className="mt-2 font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest">
                  Placed {fmtDate(order.created_at) || "—"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest">
                  {order.item_count} item{order.item_count === 1 ? "" : "s"} · {order.currency}
                </div>
                <div className="font-display text-2xl text-zinc-100 mt-1">
                  ${order.total.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="px-6 py-8" data-testid="track-status-row">
              <div className="flex items-start">
                <TimelineStep
                  label="Ordered"
                  sub={fmtDate(order.created_at) || "—"}
                  state="done"
                />
                <TimelineStep
                  label={paid ? "Paid" : "Awaiting Payment"}
                  sub={paid ? "Confirmed" : "Pending"}
                  state={paid ? "done" : "active"}
                />
                <TimelineStep
                  label={shipped ? "Shipped" : "Packing"}
                  sub={order.shipped_at ? fmtDate(order.shipped_at) : (paid ? "In progress" : "Queued")}
                  state={shipped ? "done" : paid ? "active" : "pending"}
                />
                <TimelineStep
                  label="Delivered"
                  sub={eta ? `Est. ${eta}` : "—"}
                  state="pending"
                  isLast
                />
              </div>
            </div>

            {/* Detail row */}
            <div className="border-t border-zinc-800 px-6 py-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Left: recipient + carrier */}
              <div className="space-y-4">
                <div>
                  <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-1">
                    Recipient
                  </div>
                  <div className="font-display text-base text-zinc-100 uppercase tracking-widest">
                    {order.customer_name}
                  </div>
                </div>
                {shipped ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-1">
                        Carrier
                      </div>
                      <div className="font-display text-base text-zinc-100 uppercase tracking-widest">
                        {CARRIER_LABEL[(order.tracking_carrier || "other").toLowerCase()] || "Other"}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-1">
                        Tracking #
                      </div>
                      <div
                        className="font-mono-tech text-xs text-zinc-100 break-all"
                        data-testid="track-tracking-number"
                      >
                        {order.tracking_number || "—"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-1">
                      Status
                    </div>
                    <div className="font-display text-base text-zinc-100 uppercase tracking-widest">
                      {paid ? "Preparing your order" : "Awaiting payment confirmation"}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: CTA + est delivery */}
              <div className="sm:border-l sm:border-zinc-800 sm:pl-6 flex flex-col justify-between gap-4">
                <div>
                  <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-1">
                    Estimated arrival
                  </div>
                  <div className="font-display text-xl text-zinc-100 uppercase tracking-wider">
                    {eta || (shipped ? "Any day now" : "Ships in 1–2 business days")}
                  </div>
                </div>
                {shipped && order.tracking_url ? (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="track-carrier-link"
                    className="inline-flex items-center justify-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 px-5 py-3 font-display text-sm tracking-widest uppercase transition-colors"
                  >
                    Track with carrier <ExternalLink className="w-4 h-4" />
                  </a>
                ) : (
                  <div className="font-mono-tech text-xs uppercase text-zinc-500 tracking-widest">
                    // A tracking link will appear here as soon as your package hands off to the carrier.
                  </div>
                )}
              </div>
            </div>

            {/* Footer strip */}
            <div className="border-t border-zinc-800 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest">
                Questions? <a href="/contact" className="text-zinc-300 hover:text-zinc-100 link-underline">Contact us</a>
              </div>
              <div className="font-mono-tech text-[10px] uppercase text-zinc-600 tracking-widest inline-flex items-center gap-2">
                <Package className="w-3 h-3" /> Fulfilled by PDOB · LA
              </div>
            </div>
          </div>
        )}

        {/* Placeholder helper */}
        {!order && !loading && !error && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: ShoppingBag, title: "Placed an order?", body: "Check your inbox for a confirmation email — your order number starts with PDOB-" },
              { icon: Package, title: "Fulfillment", body: "US orders ship within 1–2 business days once payment clears." },
              { icon: Truck, title: "In transit", body: "US delivery 3–5 business days · International 7–14 business days." },
            ].map((c, i) => (
              <div key={i} className="border border-zinc-800 bg-zinc-900/40 p-5">
                <c.icon className="w-5 h-5 text-zinc-300 mb-3" />
                <div className="font-display text-sm text-zinc-100 uppercase tracking-widest mb-1">
                  {c.title}
                </div>
                <div className="font-mono-tech text-[11px] uppercase text-zinc-500 tracking-widest leading-relaxed">
                  {c.body}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TrackOrderPage;
